/**
 * Utility functions for generating standardized file names for exports
 */

export interface FileNameParams {
  vertical: string
  tin?: string
  onlineName?: string
  entityId?: string
  exportType: 'main' | 'single-entity' | 'activity' | 'specific-activity'
  specificActivityType?: string
}

/**
 * Generates current timestamp in DD.MM.YYYY_HH:MM format
 */
const getCurrentTimestamp = (): string => {
  const now = new Date()
  const day = String(now.getDate()).padStart(2, '0')
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const year = now.getFullYear()
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')

  return `${day}.${month}.${year}_${hours}:${minutes}`
}

/**
 * Generates file name according to the new naming convention:
 * - Main Table: [VERTICAL_DateTime].csv
 * - Single Entity: [VERTICAL_Single_Entity]_[TIN]_[OnlineName]_DateTime.xlsx
 * - Business Activity Tab: [VERTICAL_Activity]_[TIN]_[OnlineName]_DateTime.csv
 * - Specific Activity: [VERTICAL_SpecificActivityType]_[TIN]_[OnlineName]_DateTime.csv
 */
export const generateFileName = (params: FileNameParams): string => {
  const { vertical, tin, onlineName, entityId, exportType, specificActivityType } = params
  const timestamp = getCurrentTimestamp()

  const verticalCapitalized = vertical.charAt(0).toUpperCase() + vertical.slice(1)

  switch (exportType) {
    case 'single-entity':
      const singleEntityId = tin || onlineName || entityId || 'unknown'
      return `${verticalCapitalized}_Single_Entity_${singleEntityId}_${timestamp}`

    case 'activity':
      const activityId = tin || onlineName || entityId || 'unknown'
      return `${verticalCapitalized}_Activity_${activityId}_${timestamp}`

    case 'specific-activity':
      const activityTypeCapitalized = specificActivityType
        ? specificActivityType.charAt(0).toUpperCase() + specificActivityType.slice(1)
        : 'Activity'

      const specificActivityId = tin || onlineName || entityId || 'unknown'
      return `${verticalCapitalized}_${activityTypeCapitalized}_${specificActivityId}_${timestamp}`

    case 'main':
    default:
      return `${verticalCapitalized}_${timestamp}`
  }
}

/**
 * Sanitizes file names by removing invalid characters
 */
export const sanitizeFileName = (fileName: string): string => {
  return fileName
    .replace(/[<>:"/\\|?*]/g, '_') // Replace invalid characters with underscore
    .replace(/\s+/g, '_') // Replace spaces with underscore
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .trim()
}

/**
 * Generates a complete file name with proper sanitization
 */
export const generateCompleteFileName = (params: FileNameParams): string => {
  const baseFileName = generateFileName(params)
  return sanitizeFileName(baseFileName)
}
